namespace $ {

	export class $shm_hitalama_components_catalog extends $giper_baza_entity.with({
		Entries: $giper_baza_dict_to( $giper_baza_list_link.to( ()=> $shm_hitalama_map_widget_entity_custom ) ),
		Folders: $giper_baza_list_link.to( ()=> $shm_hitalama_components_folder ),
		Locations: $giper_baza_dict_to( $giper_baza_atom_text ),
		Orders: $giper_baza_dict_to( $giper_baza_atom_list ),
	}) {
		@ $mol_mem
		static current() {
			const domain = this.$.$shm_hitalama_domain.instance()
			this.$.$shm_hitalama_map_component_ready.read( domain )
			return domain.Components_catalog()?.remote() ?? null
		}

		@ $mol_action
		static ensure() {
			const previous = this.current()
			if( previous ) return previous
			const domain = this.$.$shm_hitalama_domain.instance()
			if( !domain.can_change() ) throw new Error( 'Каталог компонентов ещё не настроен. Обратитесь к администратору домена.' )
			const catalog = $mol_wire_sync( this ).allocate( domain.link().str )
			domain.Components_catalog( null )!.remote( catalog )
			return catalog
		}

		static allocate( domain: string ) {
			// One shared access policy for the whole catalogue, not per-component UI.
			return this.$.$giper_baza_glob.land_grab( [[ null, this.$.$giper_baza_rank_make( 'post', 'just' ) ]] ).Data( this )
		}

		definition( fqn: string ) {
			this.$.$shm_hitalama_map_component_ready.read( this )
			const definitions = this.Entries()?.key( fqn )?.remote_list() ?? []
			if( definitions.length > 1 ) throw new Error( `Конфликт имени ${ fqn }: несколько определений в каталоге.` )
			return definitions[0] ?? null
		}

		definitions() {
			this.$.$shm_hitalama_map_component_ready.read( this )
			const entries = this.Entries()
			return ( entries?.keys() ?? [] ).map( name => this.definition( String( name ) ) ).filter(
				( item ): item is $shm_hitalama_map_widget_entity_custom => Boolean( item ),
			)
		}

		@ $mol_action
		register( definition: $shm_hitalama_map_widget_entity_custom ) {
			const name = definition.class_name()
			if( !/^\$[A-Za-z][A-Za-z0-9]*_[A-Za-z0-9_]+$/.test( name ) ) throw new Error( 'Укажите FQN с namespace, например ' + '$' + 'shm2_button.' )
			const previous = this.definition( name )
			if( previous && previous.link().str !== definition.link().str ) throw new Error( `Компонент ${ name } уже существует.` )
			if( !this.can_change() ) throw new Error( 'Нет прав на изменение каталога компонентов.' )
			definition.Owner_catalog( null )!.remote( this )
			const bucket = this.Entries( null )!.key( name, null )!
			if( !bucket.has( definition.link() ) ) bucket.remote_add( definition )
			return definition
		}

		@ $mol_action
		create( operation: object, name: string ) {
			name = name.trim()
			if( !/^\$[A-Za-z][A-Za-z0-9]*_[A-Za-z0-9_]+$/.test( name ) ) throw new Error( 'Укажите полное имя компонента с namespace, например ' + '$' + 'shm2_button.' )
			const fqn = name
			const registered = this.definition( fqn )
			const previousDraft = this.drafts.get( operation )
			if( registered && registered.link().str !== previousDraft?.link().str ) throw new Error( `Компонент ${ name } уже существует.` )
			const definition = $mol_wire_sync( this ).draft( operation )
			const previous = this.definition( fqn )
			if( previous && previous.link().str !== definition.link().str ) throw new Error( `Компонент ${ name } уже существует.` )
			// Initialize completely before publishing. A suspended action reuses its draft.
			definition.Class_name( null )!.val( fqn )
			definition.Owner_catalog( null )!.remote( this )
			definition.Source_format( null )!.val( 'ts' )
			definition.title( name.trim() )
			if( !definition.source_link( 'tree' ) ) definition.source_text( 'tree', `${ fqn } $mol_view\n\tsub /\n\t\t<= message \\Компонент готов\n` )
			if( !definition.source_link( 'logic' ) ) definition.source_text( 'logic', `namespace $.$$ {\n\texport class ${ fqn } extends $.${ fqn } {}\n}\n` )
			if( !definition.source_link( 'style' ) ) definition.source_text( 'style', `namespace $ {\n\t$mol_style_define( ${ fqn }, {} )\n}\n` )
			return this.register( definition )
		}

		folders() {
			this.$.$shm_hitalama_map_component_ready.read( this )
			return this.Folders()?.remote_list() ?? []
		}

		@ $mol_action
		application_remove( fqn: string ) {
			if( !this.can_change() ) throw new Error( 'Нет прав на изменение каталога приложений.' )
			const plan = $mol_wire_sync( this ).application_removal( fqn )
			for( const order of plan.orders ) order.atom.val( order.value )
			for( const target of plan.targets ) {
				target.owner?.remote( null )
				plan.locations?.cut( target.fqn )
				plan.entries?.cut( target.fqn )
			}
		}

		application_removal( fqn: string ) {
			if( !/^\$[A-Za-z][A-Za-z0-9]*_[A-Za-z0-9_]+$/.test( fqn ) ) throw new Error( 'Некорректный FQN приложения.' )
			const definitions = this.definitions().filter( item => item.class_name() === fqn || item.class_name().startsWith( fqn + '_' ) )
			for( const definition of definitions ) {
				if( !definition.can_change() ) throw new Error( `Нет прав на удаление модуля ${ definition.class_name() }.` )
			}
			const names = new Set( definitions.map( item => item.class_name() ) )
			const entries = this.Entries(), locations = this.Locations(), orders = this.Orders()
			const targets = definitions.map( definition => ({ fqn: definition.class_name(), owner: definition.Owner_catalog() }) )
			const updates = ( orders?.keys() ?? [] ).map( key => {
				const atom = orders!.key( key )!
				return { atom, value: ( atom.val() ?? [] ).filter( item => typeof item !== 'string' || !names.has( item ) ) }
			} )
			// Resolve all targets before the first write. Replay reuses this exact plan.
			entries?.keys(); locations?.keys()
			for( const target of targets ) target.owner?.remote()
			return { targets, entries, locations, orders: updates }
		}

		folder( id: string ) { return this.folders().find( folder => folder.link().str === id ) ?? null }

		location( fqn: string ) { return this.Locations()?.key( fqn )?.val() ?? '' }

		tree() {
			const definitions = this.definitions(), folders = this.folders()
			for( const item of [ ...definitions, ...folders ] ) this.$.$shm_hitalama_map_component_ready.read( item )
			const names = definitions.map( item => item.class_name() )
			return new this.$.$shm_hitalama_components_tree( names,
				folders.map( folder => ({ id: folder.link().str, parent: folder.Parent()?.val() ?? '' }) ),
				new Map( names.map( name => [ name, this.location( name ) ] ) ),
				new Map( [ '', ...folders.map( folder => folder.link().str ) ].map( id => [ id, this.Orders()?.key( id || 'root' )?.val()?.filter( ( value ): value is string => typeof value === 'string' ) ?? [] ] ) ),
			)
		}

		@ $mol_action
		node_move( id: string, parent: string, siblings: readonly string[] ) {
			if( !this.can_change() ) throw new Error( 'Нет прав на изменение каталога приложений.' )
			const tree = this.tree()
			if( !tree.movable( id ) ) throw new Error( 'Вложенный FQN-модуль нельзя перемещать отдельно.' )
			if( parent && !this.folder( parent ) ) throw new Error( 'Группа не найдена.' )
			for( let cursor = parent; cursor; cursor = tree.parents.get( cursor ) ?? '' ) {
				if( cursor === id ) throw new Error( 'Нельзя переместить группу внутрь себя.' )
			}
			const expected = [ ...( parent ? tree.children( parent ) : tree.roots ).filter( child => child !== id ), id ]
			if( siblings.length !== expected.length || new Set( siblings ).size !== expected.length || expected.some( child => !siblings.includes( child ) ) ) throw new Error( 'Список приложений изменился. Повторите перенос.' )
			// Validate and load everything before writing. Replayed writes are idempotent.
			const folder = id.startsWith( '$' ) ? null : this.folder( id )!
			if( folder ) folder.Parent( null )!.val( parent )
			else this.Locations( null )!.key( id, null )!.val( parent )
			this.Orders( null )!.key( parent || 'root', null )!.val( [ ...siblings ] )
		}

		@ $mol_action
		application_move( fqn: string, folder: string ) {
			if( !this.can_change() ) throw new Error( 'Нет прав на изменение каталога приложений.' )
			if( !this.definition( fqn ) ) throw new Error( 'Приложение не найдено.' )
			if( folder && !this.folder( folder ) ) throw new Error( 'Группа не найдена.' )
			if( !this.tree().movable( fqn ) ) throw new Error( 'Вложенный FQN-модуль нельзя перемещать отдельно.' )
			this.Locations( null )!.key( fqn, null )!.val( folder )
		}

		@ $mol_action
		folder_create( operation: object, name: string, parent = '' ) {
			name = name.trim()
			if( !name ) throw new Error( 'Укажите название группы.' )
			if( parent && !this.folder( parent ) ) throw new Error( 'Группа не найдена.' )
			const folder = $mol_wire_sync( this ).folder_draft( operation )
			folder.title( name )
			folder.Parent( null )!.val( parent )
			const list = this.Folders( null )!
			if( !list.has( folder.link() ) ) list.remote_add( folder )
			return folder
		}

		@ $mol_action
		folder_remove( id: string ) {
			if( !this.can_change() ) throw new Error( 'Нет прав на изменение каталога приложений.' )
			// Retain the original move plan if a write suspends and the action restarts.
			const plan = $mol_wire_sync( this ).folder_removal( id )
			if( !plan ) return
			for( const atom of plan.locations ) atom.val( plan.parent )
			plan.order.val( plan.siblings )
			plan.list.cut( plan.link )
		}

		folder_removal( id: string ) {
			const folder = this.folder( id )
			if( !folder ) return null
			const tree = this.tree(), parent = tree.parents.get( id ) ?? ''
			const siblings = ( parent ? tree.children( parent ) : tree.roots ).flatMap( child => child === id ? tree.children( id ) : [ child ] )
			const locations = [
				...this.folders().filter( item => item.Parent()?.val() === id ).map( item => item.Parent( null )! ),
				...this.definitions().filter( item => this.location( item.class_name() ) === id ).map( item => this.Locations( null )!.key( item.class_name(), null )! ),
			]
			const order = this.Orders( null )!.key( parent || 'root', null )!
			const list = this.Folders()!, link = folder.link()
			// Load all write targets before moving anything; never remove module entries.
			for( const atom of locations ) atom.val()
			order.val(); list.items_vary()
			return { parent, siblings, locations, order, list, link }
		}

		private folder_drafts = new WeakMap< object, $shm_hitalama_components_folder >
		folder_draft( operation: object ) {
			const previous = this.folder_drafts.get( operation )
			if( previous ) return previous
			if( !this.can_change() ) throw new Error( 'Нет прав на создание групп.' )
			const folder = this.land().area_make().Data( $shm_hitalama_components_folder )
			this.folder_drafts.set( operation, folder )
			return folder
		}

		private drafts = new WeakMap< object, $shm_hitalama_map_widget_entity_custom >
		draft( operation: object ) {
			const previous = this.drafts.get( operation )
			if( previous ) return previous
			if( !this.can_change() ) throw new Error( 'Нет прав на создание компонентов.' )
			const definition = this.land().area_make().Data( $shm_hitalama_map_widget_entity_custom )
			this.drafts.set( operation, definition )
			return definition
		}
	}
}
